"use client";
import {
	type MotionValue,
	motion,
	motionValue,
	useSpring,
	useTransform,
} from "motion/react";
import { useEffect, useId, useState } from "react";
import React from "react";
import useMeasure from "react-use-measure";

const TRANSITION = {
	type: "spring",
	stiffness: 300,
	damping: 30,
	mass: 0.5,
};

function Digit({ value, place }: { value: number; place: number }) {
	const valueRoundedToPlace = Math.floor(value / place) % 10;
	const [prevValue, setPrevValue] = useState(valueRoundedToPlace);
	// Ensure we have an initial value for the animation
	const initial = motionValue(prevValue);
	const animatedValue = useSpring(initial, TRANSITION);

	// Track previous value to determine animation direction
	useEffect(() => {
		if (valueRoundedToPlace !== prevValue) {
			setPrevValue(valueRoundedToPlace);
			animatedValue.set(valueRoundedToPlace);
		}
	}, [animatedValue, valueRoundedToPlace, prevValue]);

	return (
		<div className="relative inline-block w-[1ch] overflow-hidden leading-none tabular-nums">
			{/* Invisible digit for measuring height */}
			<div className="invisible">0</div>
			{/* Render all possible digits in a vertical stack */}
			{Array.from({ length: 10 }, (_, i) => (
				<NumberItem key={i} mv={animatedValue} number={i} />
			))}
		</div>
	);
}

function NumberItem({
	mv,
	number,
}: { mv: MotionValue<number>; number: number }) {
	const uniqueId = useId();
	const [ref, bounds] = useMeasure();

	// Calculate vertical position for the smooth scrolling effect
	const y = useTransform(mv, (latest) => {
		if (!bounds.height) return 0;
		
		// Get the current value and calculate how far this digit should be from it
		const currentValue = latest % 10;
		
		// Calculate shortest distance considering the circular nature of digits (0-9)
		let distanceUp = (number - currentValue) % 10;
		if (distanceUp < 0) distanceUp += 10; // Ensure positive distance
		
		let distanceDown = (currentValue - number) % 10;
		if (distanceDown < 0) distanceDown += 10; // Ensure positive distance
		
		// For flipping up animation: use negative values
		// For increasing values (1->2), we want to scroll up (negative y)
		// For decreasing values (9->0), we want to scroll down (positive y)
		let distance: number;
		if (distanceUp <= distanceDown) {
			// Shortest path is up
			distance = -distanceUp;
		} else {
			// Shortest path is down
			distance = distanceDown;
		}
		
		// Position based on distance
		return distance * bounds.height;
	});

	// Don't render the animated number until we know the height
	if (!bounds.height) {
		return (
			<span ref={ref} className="invisible absolute">
				{number}
			</span>
		);
	}

	return (
		<motion.span
			style={{ y }}
			layoutId={`${uniqueId}-${number}`}
			className="absolute inset-0 flex items-center justify-center font-medium"
			transition={TRANSITION}
			ref={ref}
		>
			{number}
		</motion.span>
	);
}

type SlidingNumberProps = {
	value: number;
	padStart?: boolean;
	decimalSeparator?: string;
};

export function SlidingNumber({
	value,
	padStart = false,
	decimalSeparator = ".",
}: SlidingNumberProps) {
	// Handle negative values properly
	const isNegative = value < 0;
	const absValue = Math.abs(value);
	
	// Split the value into integer and decimal parts
	const [integerPart, decimalPart] = absValue.toString().split(".");
	const integerValue = Number.parseInt(integerPart, 10);
	
	// Add padding for single-digit numbers if requested
	const paddedInteger =
		padStart && integerValue < 10 ? `0${integerPart}` : integerPart;
	
	// Split into individual digits
	const integerDigits = paddedInteger.split("");
	const integerPlaces = integerDigits.map((_, i) =>
		10 ** (integerDigits.length - i - 1),
	);

	return (
		<div className="flex items-center">
			{/* Show negative sign if value is negative */}
			{isNegative && <span className="mr-1">-</span>}
			
			{/* Render integer part digits */}
			{integerDigits.map((_, index) => (
				<Digit
					key={`pos-${integerPlaces[index]}`}
					value={integerValue}
					place={integerPlaces[index]}
				/>
			))}
			
			{/* Render decimal part if it exists */}
			{decimalPart && (
				<>
					<span className="mx-[0.125em]">{decimalSeparator}</span>
					{decimalPart.split("").map((_, index) => {
						// For decimals, we need to use a different approach
						// Calculate value to ensure proper animation
						const decimalValue = Number.parseFloat(`0.${decimalPart}`) * (10 ** (index + 1));
						
						return (
							<Digit
								key={`decimal-${index + 1}`}
								value={decimalValue}
								place={1}
							/>
						);
					})}
				</>
			)}
		</div>
	);
}

// Add display name
SlidingNumber.displayName = "SlidingNumber";
